/**
 * Keep keyboard focus inside an open dialog, and give it back on close.
 *
 * F11. Every modal in this app already set `role="dialog"` and `aria-modal`,
 * and most closed on Escape — but there was no Tab handling anywhere in the
 * codebase. `aria-modal` is a PROMISE to assistive technology that the rest of
 * the page is inert; it does not make it so. So a keyboard or screen-reader
 * user opening the fund-research dialog could tab straight past its last button
 * into the page behind it, land on controls the overlay was visually covering,
 * and operate them blind. The dialog looked modal and behaved like a floating
 * div.
 *
 * Three things, which is all a trap actually needs:
 *   1. focus moves INTO the dialog when it opens (otherwise the first Tab goes
 *      to the browser chrome or the top of the page behind);
 *   2. Tab and Shift+Tab wrap at the ends instead of escaping;
 *   3. focus RETURNS to whatever opened the dialog when it closes — without
 *      this, closing a modal dumps a keyboard user back at the top of the
 *      document and they have to tab all the way back to where they were.
 *
 * Deliberately queries focusable elements on each Tab rather than caching them:
 * these dialogs load their content async, so a list captured at open time is
 * wrong by the time anyone presses a key.
 */
import { useEffect, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if (el.hasAttribute("disabled")) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    // Visibility: use checkVisibility() where the browser has it, and treat
    // the element as focusable everywhere else.
    //
    // This used to test `el.offsetParent !== null`, which was wrong in two
    // directions. It reports null for any position:fixed element — and these
    // dialogs are fixed — and it is ALWAYS null under jsdom, which has no
    // layout. So the filter could empty the list entirely, leaving the trap
    // with nothing to focus and Tab free to walk out of the dialog: the exact
    // failure the trap exists to prevent. Including a hidden control by
    // mistake is a far cheaper error than excluding every visible one.
    const check = (el as { checkVisibility?: (o?: object) => boolean }).checkVisibility;
    if (typeof check === "function") {
      return check.call(el, { checkOpacity: false, checkVisibilityCSS: true });
    }
    return true;
  });
}

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    // Remember who opened us, so we can hand focus back.
    const opener = document.activeElement as HTMLElement | null;

    // Move focus in. Prefer the first natural control; fall back to the dialog
    // itself, which needs tabIndex={-1} to be programmatically focusable.
    const first = focusable(root)[0];
    (first ?? root).focus?.();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const items = focusable(root);
      if (items.length === 0) {
        // Nothing to focus, but focus must still not leave.
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;

      if (!root.contains(activeEl)) {
        e.preventDefault();
        firstEl.focus();
        return;
      }
      if (e.shiftKey && activeEl === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && activeEl === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      // Give focus back — but only if it is still somewhere in the dialog we
      // are tearing down. If the user has already clicked elsewhere, stealing
      // it back would be its own bug.
      if (opener && document.contains(opener)) {
        const activeEl = document.activeElement;
        if (!activeEl || activeEl === document.body || root.contains(activeEl)) {
          opener.focus?.();
        }
      }
    };
  }, [ref, active]);
}
